import { useQuery } from '@apollo/client';
import { CarouselItemId } from '@homex/se-widget-flow-types';
import { loader } from 'graphql.macro';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { setCurrentCarouselItem, setPreferredPartner } from 'actions';
import { CarouselContinueButton } from 'components/CarouselContinueButton';
import { Phrase } from 'components/Phrase';
import { PreferredPartnerRadioButton } from 'components/PreferredPartnerRadioButton';
import { ReactComponent as InputErrorSvg } from 'components/svg/InputError.svg';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import {
  CarouselBottomButtonType,
  CarouselReduxReducer,
  IState,
  ServiceType,
} from 'typings';
import {
  IAvailablePartnersForTimeslotQuery,
  IAvailablePartnersForTimeslotQueryVariables,
} from 'typings/GraphQL';

import {
  Buttons,
  Container,
  Description,
  ErrorContainer,
  ErrorDescription,
  ErrorSubcontractor,
  FormGroup,
} from './styles';

const availablePartnersForTimeslotQuery = loader(
  '../../queries/availablePartnersForTimeslot.graphql',
);

export const CarouselItemAvailablePartnersBottom = () => {
  const dispatch = useThunkDispatch();
  const {
    addressId,
    currentItemId,
    isEmergency,
    preferredPartner,
    serviceCodeId,
    serviceType,
    startTime,
    subcontractor,
  } = useSelector((state: IState) => ({
    addressId: state.userInput.address.id,
    currentItemId: state.serviceCarousel.currentItemId,
    isEmergency: state.userInput.isEmergency,
    preferredPartner: state.userInput.preferredPartner,
    serviceCodeId: state.userInput.serviceCodeId,
    serviceType: state.userInput.serviceType,
    startTime: state.userInput.selectedTimeSlot?.startTime,
    subcontractor: state.sourceTrackingAnalytics.subcontractor,
  }));

  const shouldSkipQuery =
    !addressId ||
    !serviceCodeId ||
    !serviceType ||
    !startTime ||
    currentItemId !== CarouselItemId.AvailablePartners;

  const { data, error, loading, refetch } = useQuery<
    IAvailablePartnersForTimeslotQuery,
    IAvailablePartnersForTimeslotQueryVariables
  >(availablePartnersForTimeslotQuery, {
    notifyOnNetworkStatusChange: true,
    skip: shouldSkipQuery,
    variables: {
      addressId: addressId || '',
      isEmergency,
      limit: 3,
      serviceCodeId: serviceCodeId || '',
      serviceType: serviceType || ServiceType.REPAIR,
      startTime: startTime || '',
      subcontractor,
    },
  });

  const partners = data?.availablePartnersForTimeslot?.partners || [];
  const subcontractorName =
    data?.availablePartnersForTimeslot?.subcontractor?.name;

  // Assume that only one of the partners matches the subcontractor. Find the
  // first one where `isSubcontractorMatch` is true and use that as the
  // default. Or set the default to `undefined` (non selected)
  const partnerSubcontractorMatch = partners.find(
    ({ isSubcontractorMatch }) => isSubcontractorMatch,
  );

  const isPreferredPartnerInAvailablePartners = !!partners.find(
    ({ partnerId }) => partnerId === preferredPartner?.partnerId,
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedPartner = partners.find(
      ({ partnerId }) => partnerId === event.target.value,
    );

    dispatch(setPreferredPartner(selectedPartner));
  };

  const handleContinueClick = () => {
    if (error) {
      refetch();
    } else {
      dispatch(
        setCurrentCarouselItem(
          CarouselReduxReducer.SERVICE,
          CarouselItemId.RepairSummary,
        ),
      );
    }
  };

  useEffect(() => {
    // Only change the preferred partner if one is not already selected
    if (
      !isPreferredPartnerInAvailablePartners &&
      currentItemId === CarouselItemId.AvailablePartners
    ) {
      dispatch(setPreferredPartner(partnerSubcontractorMatch));
    }
  }, [
    currentItemId,
    dispatch,
    isPreferredPartnerInAvailablePartners,
    partnerSubcontractorMatch,
  ]);

  const isSubcontractorUnavailable =
    subcontractor && !partnerSubcontractorMatch && !loading;

  return (
    <FormGroup>
      {error ? (
        <ErrorContainer>
          <InputErrorSvg />
          <ErrorDescription>
            <Phrase id="AVAILABLE_PARTNERS.ERROR" />
          </ErrorDescription>
        </ErrorContainer>
      ) : (
        <Container>
          <Description>
            <Phrase id="AVAILABLE_PARTNERS.DESCRIPTION" />
          </Description>
          {isSubcontractorUnavailable && subcontractorName && (
            <ErrorSubcontractor>
              <Phrase
                id="AVAILABLE_PARTNERS.SUBCONTRACTOR_UNAVAILABLE.TEMPLATE"
                SUBCONTRACTOR_NAME={subcontractorName}
              />
            </ErrorSubcontractor>
          )}
          {partners.map(({ partnerId, ...partnerRest }) => (
            <PreferredPartnerRadioButton
              checked={partnerId === preferredPartner?.partnerId}
              key={partnerId}
              name="partner"
              onChange={handleChange}
              partner={{ partnerId, ...partnerRest }}
              value={partnerId}
            />
          ))}
        </Container>
      )}
      <Buttons>
        <CarouselContinueButton
          buttonType={
            error
              ? CarouselBottomButtonType.RETRY
              : CarouselBottomButtonType.CONTINUE
          }
          isDisabled={!preferredPartner && !error}
          isLoading={loading}
          onClick={handleContinueClick}
        />
      </Buttons>
    </FormGroup>
  );
};
